/**
 * Xero Integration Dashboard Component
 * Main dashboard for Xero integration management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { invoiceService } from '../../services/invoiceService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from '../common/ProfessionalLoader';
import XeroConnectionStatus from './XeroConnectionStatus';
import XeroSyncStatus from './XeroSyncStatus';
import XeroAccounts from './XeroAccounts';
import './XeroIntegrationDashboard.css';

const XeroIntegrationDashboard = () => {
  const { showSuccess, showError } = useNotifications();
  const { canIntegrateXero } = useRBAC();

  // State management
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [syncStatusResponse, accountsResponse] = await Promise.all([
        invoiceService.getXeroSyncStatus(),
        invoiceService.getXeroAccounts()
      ]);

      const dashboardData = {
        syncStatus: syncStatusResponse.success ? syncStatusResponse.data : null,
        accounts: accountsResponse.success ? accountsResponse.data.accounts : []
      };

      setDashboardData(dashboardData);
    } catch (error) {
      showError('Failed to load Xero dashboard data', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load dashboard data on component mount
  useEffect(() => {
    if (canIntegrateXero()) {
      loadDashboardData();
    }
  }, [loadDashboardData, canIntegrateXero]);

  // Handle test connection
  const handleTestConnection = async () => {
    setIsProcessing(true);
    try {
      const response = await invoiceService.testXeroConnection();

      if (response.success) {
        showSuccess('Xero connection test successful', 'Success');
        loadDashboardData();
      } else {
        showError(response.message || 'Connection test failed', 'Error');
      }
    } catch (error) {
      showError('Connection test failed', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle sync all invoices
  const handleSyncAllInvoices = async () => {
    setIsProcessing(true);

    try {
      const response = await invoiceService.syncInvoicesToXero();

      if (response.success) {
        const data = response.data || response;
        showSuccess(
          data.message || `Bulk sync started for ${data.total_invoices || 0} invoices. The sync is running in the background. You can navigate away and check back later.`,
          'Sync Started',
          { duration: 10000 }
        );

        // Optionally reload dashboard data to show updated status
        setTimeout(() => {
          loadDashboardData();
        }, 2000);
      } else {
        showError(response.message || response.error || 'Failed to start sync', 'Error');
      }
    } catch (error) {
      showError(error.message || 'Failed to start sync', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };


  if (!canIntegrateXero()) {
    return (
      <div className="xero-integration-dashboard">
        <div className="access-denied">
          <h3>Access Denied</h3>
          <p>You do not have permission to access Xero integration.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="xero-integration-dashboard">
        <div className="loading-container">
          <ProfessionalLoader message="Loading Xero integration dashboard..." size="large" variant="default" />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="xero-integration-dashboard">
        <div className="error-state">
          <h3>Failed to Load Dashboard</h3>
          <p>Unable to load Xero integration dashboard data.</p>
          <button className="btn btn-primary" onClick={loadDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { syncStatus = {}, accounts = [] } = dashboardData || {};

  // Ensure syncStatus is always an object with default values
  const safeSyncStatus = {
    total_invoices: 0,
    synced_invoices: 0,
    pending_invoices: 0,
    failed_invoices: 0,
    not_synced_invoices: 0,
    ...syncStatus
  };

  return (
    <div className="xero-integration-dashboard">
      {/* Header */}
      <div className="xero-integration-dashboard-header">
        <div className="header-content">
          <h2>💰 Xero Integration Dashboard</h2>
          <p>Manage Xero accounting integration, invoice synchronization, and financial operations</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-outline"
            onClick={handleTestConnection}
            disabled={isProcessing}
          >
            {isProcessing ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSyncAllInvoices}
            disabled={isProcessing}
          >
            {isProcessing ? 'Syncing...' : 'Sync All Invoices'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'connection' ? 'active' : ''}`}
          onClick={() => setActiveTab('connection')}
        >
          🔗 Connection
        </button>
        <button
          className={`tab-button ${activeTab === 'sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('sync')}
        >
          🔄 Sync Status
        </button>
        <button
          className={`tab-button ${activeTab === 'accounts' ? 'active' : ''}`}
          onClick={() => setActiveTab('accounts')}
        >
          🏢 Accounts
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Quick Stats */}
            <div className="stats-section">
              <h3>Quick Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📄</div>
                  <div className="stat-content">
                    <h4>Total Invoices</h4>
                    <p className="stat-value">{safeSyncStatus.total_invoices}</p>
                  </div>
                </div>

                <div className="stat-card synced">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <h4>Synced</h4>
                    <p className="stat-value">{safeSyncStatus.synced_invoices}</p>
                  </div>
                </div>

                <div className="stat-card pending">
                  <div className="stat-icon">⏳</div>
                  <div className="stat-content">
                    <h4>Pending</h4>
                    <p className="stat-value">{safeSyncStatus.pending_invoices}</p>
                  </div>
                </div>

                <div className="stat-card failed">
                  <div className="stat-icon">❌</div>
                  <div className="stat-content">
                    <h4>Failed</h4>
                    <p className="stat-value">{safeSyncStatus.failed_invoices}</p>
                  </div>
                </div>

                <div className="stat-card not-synced">
                  <div className="stat-icon">🚫</div>
                  <div className="stat-content">
                    <h4>Not Synced</h4>
                    <p className="stat-value">{safeSyncStatus.not_synced_invoices}</p>
                  </div>
                </div>

                <div className="stat-card accounts">
                  <div className="stat-icon">🏢</div>
                  <div className="stat-content">
                    <h4>Xero Accounts</h4>
                    <p className="stat-value">{accounts.length}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'connection' && (
          <XeroConnectionStatus
            onTestConnection={handleTestConnection}
            isProcessing={isProcessing}
          />
        )}

        {activeTab === 'sync' && (
          <XeroSyncStatus
            syncStatus={safeSyncStatus}
            onSyncAll={handleSyncAllInvoices}
            isProcessing={isProcessing}
          />
        )}

        {activeTab === 'accounts' && (
          <XeroAccounts accounts={accounts} />
        )}

      </div>
    </div>
  );
};

export default XeroIntegrationDashboard;
